import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Search } from "lucide-react";
import { API } from "@/lib/api";
import { Show } from "@clerk/react";
import { toast } from "sonner";
import { Video, Building2 } from "lucide-react";
import { useApi, fmtDate, fmtTime, errMsg } from "@/lib/api";

export const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800", confirmed: "bg-teal-100 text-teal-800",
  completed: "bg-sky-100 text-sky-800", cancelled: "bg-rose-100 text-rose-700",
};

const Row = ({ b, onCancel }) => (
  <div className="flex flex-col gap-4 rounded-[1.75rem] border-2 border-slate-800/5 bg-white p-6 sm:flex-row sm:items-center" data-testid="my-booking-card">
    <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl ${b.type === "online" ? "bg-[#e0f2fe]" : "bg-[#fff1f1]"}`}>
      {b.type === "online" ? <Video className="h-6 w-6" /> : <Building2 className="h-6 w-6" />}
    </div>
    <div className="flex-1">
      <p className="font-display text-lg font-semibold text-slate-800">{b.child_name ? `${b.child_name} · ${b.service}` : `${b.type === "online" ? "Online consultation" : "Clinic visit"} · ${b.ref}`}</p>
      <p className="text-sm text-slate-500">{fmtDate(b.date)} at {fmtTime(b.time)}{b.child_name && ` · Ref ${b.ref}`}</p>
      {!b.child_name && b.type === "online" && b.status === "confirmed" && <p className="mt-1 text-xs font-semibold text-teal-700">Your video link was sent to your email.</p>}
      {b.type === "online" && b.meeting_link && b.status === "confirmed" && (
        <a href={b.meeting_link} target="_blank" rel="noreferrer" data-testid="my-booking-join-link" className="mt-2 inline-block text-sm font-bold text-teal-700 underline">Join video consultation</a>
      )}
    </div>
    <span className={`w-fit rounded-full px-3 py-1 text-xs font-bold capitalize ${STATUS_STYLE[b.status]}`} data-testid="my-booking-status">{b.status}</span>
    {onCancel && ["pending", "confirmed"].includes(b.status) && (
      <button onClick={() => onCancel(b.id)} data-testid="my-booking-cancel" className="text-sm font-bold text-rose-600 hover:underline">Cancel</button>
    )}
  </div>
);

const List = () => {
  const api = useApi();
  const [items, setItems] = useState(null);
  const load = useCallback(() => api("get", "/bookings/mine").then(setItems).catch(() => setItems([])), [api]);
  useEffect(() => { load(); }, [load]);
  const cancel = async (id) => {
    if (!window.confirm("Cancel this appointment?")) return;
    try { await api("post", `/bookings/${id}/cancel`); toast.success("Appointment cancelled"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mx-auto max-w-4xl px-6 py-14" data-testid="my-appointments-page">
      <h1 className="text-4xl font-bold tracking-tight">My visits</h1>
      <p className="mt-2 text-sm text-slate-500">Appointments you booked while signed in.</p>
      <div className="mt-8 space-y-4">
        {items === null && <p className="text-slate-500">Loading...</p>}
        {items?.length === 0 && (
          <div className="rounded-[1.75rem] bg-[#fef7e5] p-8 text-center" data-testid="my-appointments-empty">
            <p className="font-semibold text-slate-700">No visits yet.</p>
            <Link to="/book" className="mt-4 inline-block rounded-full bg-teal-600 px-6 py-3 text-sm font-bold text-white">Book one now</Link>
          </div>
        )}
        {items?.map((b) => <Row key={b.id} b={b} onCancel={cancel} />)}
      </div>
    </div>
  );
};

const Lookup = () => {
  const [contact, setContact] = useState("");
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const find = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { setItems((await axios.post(`${API}/bookings/lookup`, { contact })).data); } catch (err) { setItems(null); toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <div className="mx-auto max-w-4xl px-6 py-14" data-testid="booking-lookup">
      <h2 className="text-3xl font-bold tracking-tight">Check your bookings</h2>
      <p className="mt-2 text-sm text-slate-500">Booked without signing in? Enter the mobile number or email you used. For privacy we only show the date, time and status. Video links are sent to your email.</p>
      <form onSubmit={find} className="mt-6 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1"><Search className="absolute left-4 top-4 h-4 w-4 text-slate-400" />
          <input value={contact} onChange={(e) => setContact(e.target.value)} placeholder="98200 00000 or you@email.com" data-testid="lookup-contact-input"
            className="h-12 w-full rounded-full border-2 border-slate-200 bg-white pl-10 pr-4 text-sm outline-none focus:border-teal-500" /></div>
        <button disabled={busy || contact.trim().length < 5} data-testid="lookup-submit-button"
          className="btn-pop h-12 rounded-full bg-teal-600 px-7 text-sm font-bold text-white disabled:opacity-40">{busy ? "Searching..." : "Find my bookings"}</button>
      </form>
      <div className="mt-6 space-y-4" data-testid="lookup-results">
        {items?.length === 0 && <p className="rounded-2xl bg-[#fef7e5] p-5 text-sm font-semibold text-slate-700" data-testid="lookup-empty">No bookings found for that number or email.</p>}
        {items?.map((b) => <Row key={b.id} b={b} />)}
      </div>
    </div>
  );
};

export default function MyAppointments() {
  return (
    <>
      <Show when="signed-in"><List /></Show>
      <Lookup />
      <Show when="signed-out">
        <p className="pb-10 text-center text-sm text-slate-500">Prefer one place for all visits? <Link to="/sign-in" className="font-bold text-teal-700 underline" data-testid="lookup-sign-in-link">Sign in</Link> before booking.</p>
      </Show>
    </>
  );
}
